'use client';

import { ArrowLeft, ArrowRight, Bookmark, Check, ChevronDown, ExternalLink, PenLine, Send, SkipForward, Zap } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/navigation/dropdown-menu';
import { cn } from '@/lib/utils';
import type { CardDecision, DeckCardKind, MessageState } from '../types';

interface CardActionBarProps {
  message: MessageState;
  paywallMode: boolean;
  disabled: boolean;
  /** 'job' cards apply instead of send, and don't wait on a message. */
  cardKind: DeckCardKind;
  currentDecision?: CardDecision;
  canGoPrev: boolean;
  canGoNext: boolean;
  /** Company progress: which company (1-based) of how many. */
  groupIndex: number;
  groupsTotal: number;
  /** Fill fraction for the progress separator (0–100), over the contact sequence. */
  progressPct: number;
  isStreaming: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
  onApprove: () => void;
  /**
   * When provided (footer variant), the Pass button becomes a split button: the
   * main body still passes the current contact (`onSkip`), and an attached caret
   * opens a menu to pass the whole open role (`onPassJob`) or the whole company
   * (`onPassCompany`). Omitted on Simple Search, which keeps the plain Pass button.
   */
  onPassJob?: () => void;
  onPassCompany?: () => void;
  /** Whether the "Pass this job" menu item applies (there's an open role on the card). */
  showPassJob?: boolean;
  /**
   * When provided (footer variant, contact cards), the "Approve & Send" button
   * becomes a split button mirroring the Pass split: the main body still runs the
   * normal send (`onApprove`), and an attached caret opens a menu whose "1-Click
   * Apply" item auto-submits the application for the card's anchored job
   * (`onApplyJob`) — the same branded 1-Click Apply job cards use. This makes it
   * reachable even on a contact card. Ignored on job cards (whose primary action
   * is already 1-Click Apply).
   */
  onApplyJob?: () => void;
  /**
   * Secondary "Apply" item in the same caret menu: a plain apply that opens the
   * job posting so the user applies themselves (recorded as Applied), as opposed
   * to `onApplyJob`'s branded auto-submit. Rendered below — and de-emphasized
   * relative to — 1-Click Apply. Optional; when omitted only 1-Click Apply shows.
   */
  onApplyJobExternal?: () => void;
  /** Whether the "1-Click Apply" menu item applies (there's an anchored role on the card). */
  showApplyJob?: boolean;
  /**
   * Label on the skip/pass button. Defaults to "Skip" (Simple Search); the
   * Automations queue passes "Pass" to match its "left to pass" copy.
   */
  skipLabel?: string;
  /**
   * When provided, renders a "Save opportunity" button between Pass and Approve.
   * Persists the suggestion as SAVED without sending or applying — the user is
   * interested but not acting now. Shows in the footer variant only.
   */
  onSave?: () => void;
  /**
   * When provided, renders an explicit Edit button between Pass and Approve and
   * wires it to this handler. Omitted on Simple Search (edit lives on the card).
   */
  onEdit?: () => void;
  /**
   * When true, shows subtle desktop keyboard hints under the controls
   * (← Pass · E Edit · → Approve). Opt-in so Simple Search is unaffected.
   */
  showKeyboardHints?: boolean;
  /**
   * Presentation. 'tray' (default) is Simple Search's floating bordered+shadowed
   * control block. 'footer' is the Automations layout: a flat strip attached to
   * the bottom of the outreach card — warm-white fill, a thin progress accent as
   * the top border, no prev/next buttons (swipe / queue / keyboard navigate), and
   * just the decision actions. Opt-in so Simple Search is untouched.
   */
  variant?: 'tray' | 'footer';
  /**
   * On job cards, make the primary action "1-Click Apply" (auto-submit) instead of
   * "Apply". The `onApprove` handler is what runs, so the caller wires it to its
   * 1-Click Apply flow. Defaults to false (Simple Search keeps the plain "Apply").
   */
  superApplyJob?: boolean;
}

export function CardActionBar({
  message,
  paywallMode,
  disabled,
  cardKind,
  currentDecision,
  canGoPrev,
  canGoNext,
  groupIndex,
  groupsTotal,
  progressPct,
  isStreaming,
  onPrev,
  onNext,
  onSkip,
  onApprove,
  onPassJob,
  onPassCompany,
  showPassJob = false,
  onApplyJob,
  onApplyJobExternal,
  showApplyJob = false,
  skipLabel = 'Skip',
  onSave,
  onEdit,
  showKeyboardHints = false,
  variant = 'tray',
  superApplyJob = false,
}: CardActionBarProps) {
  const isJobCard = cardKind === 'job';
  // Job-card primary action label/icon: "1-Click Apply" (opt-in) or plain "Apply".
  const jobPrimaryLabel = superApplyJob ? '1-Click Apply' : 'Apply';
  const JobPrimaryIcon = superApplyJob ? Zap : ExternalLink;
  const jobPrimaryAria = superApplyJob ? '1-Click Apply — auto-submit this application' : 'Apply to this role';
  const alreadySent = currentDecision === 'sent';
  const alreadyPassed = currentDecision === 'discarded';
  // Job cards apply directly (no message to wait on); contact cards need a ready message.
  const approveDisabled = isJobCard
    ? disabled
    : disabled || alreadySent || (!paywallMode && message.status !== 'ready');
  const pct = Math.min(100, Math.max(0, Math.round(progressPct)));

  // Footer variant — a flat strip attached under the card. The chrome (side +
  // bottom borders) is supplied by the parent so card + chips + footer read as
  // one continuous unit; here we add only the top progress accent + actions.
  if (variant === 'footer') {
    return (
      <div className="relative bg-muted/30">
        {/* Thin progress accent doubling as the footer's top border. */}
        <div className="absolute inset-x-0 top-0 h-0.5 bg-border">
          <div
            className="h-full bg-primary transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center gap-1.5 px-2 py-2 sm:gap-3 sm:px-4 sm:py-2.5">
          <span className="hidden shrink-0 tabular-nums text-caption text-muted-foreground sm:inline">
            {groupsTotal === 0 ? 0 : Math.min(groupIndex + 1, groupsTotal)}/{groupsTotal}
            {isStreaming ? '+' : ''}
          </span>

          {/* Save renders first (left of Pass) on both breakpoints — icon-only on
              mobile to leave room for Pass + Approve & Send; full "Save" label
              returns at sm+. */}
          {onSave ? (
            <Button
              type="button"
              variant="outline"
              onClick={onSave}
              disabled={disabled || alreadySent}
              className="gap-1.5 max-sm:px-2.5"
              aria-label="Save opportunity"
              title="Save opportunity"
            >
              <Bookmark className="h-4 w-4" />
              <span className="hidden sm:inline">Save</span>
            </Button>
          ) : null}

          {onPassCompany || onPassJob ? (
            // Split button: the body passes the current contact; the caret opens
            // "Pass this job" / "Pass this company".
            <div className="flex items-stretch">
              <Button
                type="button"
                variant="outline"
                onClick={onSkip}
                disabled={disabled || alreadySent}
                className={cn('gap-1.5 border-r-0 max-sm:text-xs max-sm:normal-case max-sm:tracking-normal', alreadyPassed && 'text-muted-foreground')}
                aria-label={`${skipLabel} / pass on this card`}
                title={skipLabel}
              >
                <SkipForward className="h-4 w-4" />
                {alreadyPassed ? 'Passed' : skipLabel}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={disabled || alreadySent}
                    className="w-8 shrink-0 max-sm:text-xs max-sm:normal-case max-sm:tracking-normal"
                    aria-label="More pass options"
                    title="More pass options"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  {showPassJob && onPassJob ? (
                    <DropdownMenuItem onSelect={onPassJob}>
                      <SkipForward />
                      Pass this job
                    </DropdownMenuItem>
                  ) : null}
                  {onPassCompany ? (
                    <DropdownMenuItem onSelect={onPassCompany}>
                      <SkipForward />
                      Pass this company
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={onSkip}
              disabled={disabled || alreadySent}
              className={cn('gap-1.5 max-sm:text-xs max-sm:normal-case max-sm:tracking-normal', alreadyPassed && 'text-muted-foreground')}
              aria-label={`${skipLabel} / pass on this card`}
              title={skipLabel}
            >
              <SkipForward className="h-4 w-4" />
              {alreadyPassed ? 'Passed' : skipLabel}
            </Button>
          )}

          {onEdit ? (
            <Button
              type="button"
              variant="outline"
              onClick={onEdit}
              disabled={disabled || alreadySent || (!isJobCard && message.status !== 'ready')}
              className="gap-1.5"
              aria-label="Edit this outreach"
              title="Edit"
            >
              <PenLine className="h-4 w-4" />
              Edit
            </Button>
          ) : null}

          {!isJobCard && onApplyJob && showApplyJob ? (
            // Split button (mirrors the Pass split above): the body runs the normal
            // send; the caret opens "1-Click Apply", the branded auto-submit for the
            // card's anchored job — reachable even on a contact card.
            <div className="flex flex-1 items-stretch">
              <Button
                type="button"
                variant="default"
                onClick={onApprove}
                disabled={approveDisabled}
                className="flex-1 gap-2 border-r-0 max-sm:text-xs max-sm:normal-case max-sm:tracking-normal"
                aria-label={alreadySent ? 'Already sent' : 'Approve and send'}
              >
                {alreadySent ? <Check /> : <Send />}
                {alreadySent ? (
                  'Sent'
                ) : (
                  <>
                    {/* "Approve & Send" overflows next to Pass + the icon-only Save
                        button on mobile widths — shorten to "Send" below sm. */}
                    <span className="sm:hidden">Send</span>
                    <span className="hidden sm:inline">Approve & Send</span>
                  </>
                )}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="default"
                    size="icon"
                    disabled={disabled || alreadySent}
                    // Both halves are solid burgundy, so unlike the outline Pass
                    // split there's no visible seam. A subtle left divider in the
                    // button's own foreground colour makes the caret clearly a
                    // separate control, mirroring the Pass split's border.
                    className="w-8 shrink-0 border-l border-primary-foreground/30 max-sm:text-xs max-sm:normal-case max-sm:tracking-normal"
                    aria-label="More send options"
                    title="More send options"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* 1-Click Apply is the emphasized, recommended path (branded
                      auto-submit): primary colour + semibold + a short hint. */}
                  <DropdownMenuItem
                    onSelect={onApplyJob}
                    className="text-primary"
                  >
                    <Zap className="text-primary" />
                    <span className="flex flex-col">
                      <span className="font-semibold">1-Click Apply</span>
                      <span className="text-caption text-muted-foreground">
                        We submit it for you
                      </span>
                    </span>
                  </DropdownMenuItem>
                  {onApplyJobExternal ? (
                    <DropdownMenuItem onSelect={onApplyJobExternal}>
                      <ExternalLink />
                      Apply on the job site
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Button
              type="button"
              variant="default"
              onClick={onApprove}
              disabled={approveDisabled}
              className="flex-1 gap-2 max-sm:text-xs max-sm:normal-case max-sm:tracking-normal"
              aria-label={isJobCard ? jobPrimaryAria : alreadySent ? 'Already sent' : 'Approve and send'}
            >
              {/* Job primary must honor superApplyJob (via JobPrimaryIcon/jobPrimaryLabel),
                  exactly like the `tray` variant below — NOT a hardcoded "Apply". When the
                  caller wires onApprove to the 1-Click/Super Apply flow (e.g. the home
                  outreach queue), this renders "⚡ 1-Click Apply"; the external-apply
                  surfaces keep "↗ Apply" by passing superApplyJob={false}. */}
              {isJobCard ? <JobPrimaryIcon /> : alreadySent ? <Check /> : <Send />}
              {isJobCard ? (
                jobPrimaryLabel
              ) : alreadySent ? (
                'Sent'
              ) : (
                <>
                  {/* "Approve & Send" overflows next to Pass + the icon-only Save
                      button on mobile widths — shorten to "Send" below sm. */}
                  <span className="sm:hidden">Send</span>
                  <span className="hidden sm:inline">Approve & Send</span>
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    // Floating control block — no full-width band. A bordered, shadowed tray
    // (no fill) keeps the controls together and aligns to the card's width.
    <div className="px-0 sm:px-6">
      <div
        className="mx-auto w-full max-w-lg overflow-hidden border border-border"
        style={{ boxShadow: 'var(--shadow-warm-sm)' }}
      >
        {/* Progress bar across the top of the tray */}
        <div className="relative h-px w-full bg-border">
          <div
            className="absolute inset-y-0 left-0 bg-primary transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%`, height: 2 }}
          />
        </div>

        <div className="flex items-center gap-2 p-2 sm:gap-3 sm:p-3">
        <span className="shrink-0 tabular-nums text-caption text-muted-foreground">
          {groupsTotal === 0 ? 0 : Math.min(groupIndex + 1, groupsTotal)}/{groupsTotal}
          {isStreaming ? '+' : ''}
        </span>

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onPrev}
          disabled={!canGoPrev}
          aria-label="Previous contact"
          title="Previous"
        >
          <ArrowLeft />
        </Button>

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onNext}
          disabled={!canGoNext}
          aria-label="Next contact"
          title="Next"
        >
          <ArrowRight />
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={onSkip}
          disabled={disabled || alreadySent}
          className={cn('gap-1.5', alreadyPassed && 'text-muted-foreground')}
          aria-label={`${skipLabel} / pass on this card`}
          title={skipLabel}
        >
          <SkipForward className="h-4 w-4" />
          {alreadyPassed ? 'Passed' : skipLabel}
        </Button>

        {onEdit ? (
          <Button
            type="button"
            variant="outline"
            onClick={onEdit}
            disabled={disabled || alreadySent || (!isJobCard && message.status !== 'ready')}
            className="gap-1.5"
            aria-label="Edit this outreach"
            title="Edit"
          >
            <PenLine className="h-4 w-4" />
            Edit
          </Button>
        ) : null}

        <Button
          type="button"
          variant="default"
          onClick={onApprove}
          disabled={approveDisabled}
          className="flex-1 gap-2"
          aria-label={isJobCard ? jobPrimaryAria : alreadySent ? 'Already sent' : 'Approve and send'}
        >
          {isJobCard ? <JobPrimaryIcon /> : alreadySent ? <Check /> : <Send />}
          {isJobCard ? jobPrimaryLabel : alreadySent ? 'Sent' : 'Approve & Send'}
        </Button>
        </div>

        {/* Desktop hints — opt-in (Automations). Hidden on touch widths.
            ← / → are the swipe directions (the pile's Pass / Send stamps); E is
            the only key wired by the deck, so it gets the <kbd> treatment. */}
        {showKeyboardHints ? (
          <div className="hidden items-center justify-center gap-3 border-t border-border px-3 py-1.5 text-caption text-muted-foreground sm:flex">
            <span>
              <span className="font-semibold text-foreground">←</span> Pass
            </span>
            {onEdit ? (
              <span>
                <kbd className="font-sans font-semibold text-foreground">E</kbd> Edit
              </span>
            ) : null}
            <span>
              <span className="font-semibold text-foreground">→</span> Approve
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
